// In-memory image buffer used by the create wizard's step 3.
//
// The property doesn't exist yet during create, so we can't POST
// images to a real id. Instead we collect File objects in component
// state, render them as previews via {@code URL.createObjectURL}, and
// expose drag-to-reorder via @dnd-kit. After the wizard's final
// "Xuất bản" the consuming page POSTs /properties then uploads each
// buffered file to the new id in the order the user dragged into.
//
// On edit the wizard mounts the full {@link ImageUploader} instead —
// that one talks to the real /images endpoint.

import { useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X } from "lucide-react";

import { cn } from "@/lib/utils";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB matches the backend cap.

export interface PendingImage {
  /** Stable id used by @dnd-kit. Generated locally. */
  id: string;
  file: File;
  /** Object URL for the thumbnail. Revoked when the item is removed. */
  previewUrl: string;
}

interface PendingImageBufferProps {
  images: readonly PendingImage[];
  onChange: (next: PendingImage[]) => void;
}

/**
 * Drop-zone + buffered-thumbnail grid for the create flow.
 *
 * Caller owns the buffer state AND the object-URL lifecycle: this
 * component only creates URLs when files are added and revokes them
 * when items are explicitly removed. We deliberately do NOT revoke on
 * unmount, because the buffer unmounts every time the user navigates
 * to another wizard step — revoking there would kill the previews
 * for files still held in the wizard's state. The wizard runs a
 * blanket revoke on its own unmount instead.
 */
export function PendingImageBuffer({ images, onChange }: PendingImageBufferProps) {
  const addFiles = (files: FileList | File[]) => {
    const accepted: PendingImage[] = [];
    for (const file of Array.from(files)) {
      if (!(ACCEPTED_TYPES as readonly string[]).includes(file.type)) continue;
      if (file.size > MAX_BYTES) continue;
      accepted.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (accepted.length > 0) onChange([...images, ...accepted]);
  };

  const removeImage = (id: string) => {
    const target = images.find((i) => i.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onChange(images.filter((i) => i.id !== id));
  };

  return (
    <div className="flex flex-col gap-3">
      <DropZone onPick={addFiles} />
      {images.length > 0 ? (
        <SortableGrid
          images={images}
          onReorder={onChange}
          onRemove={removeImage}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drop zone — click to open file picker OR drag-and-drop files in
// ---------------------------------------------------------------------------

function DropZone({ onPick }: { onPick: (files: FileList | File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) onPick(e.dataTransfer.files);
      }}
      className={cn(
        "flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-border bg-muted/40",
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Upload aria-hidden="true" className="size-5" />
      </div>
      <p className="text-[14px] font-semibold">Kéo thả ảnh vào đây</p>
      <p className="text-[12px] text-muted-foreground">hoặc</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="rounded-lg bg-primary px-5 py-2 text-[14px] font-semibold text-primary-foreground hover:bg-primary/90"
      >
        Chọn từ máy tính
      </button>
      <p className="font-mono text-[12px] text-ink-faint">
        PNG, JPG, WEBP · Tối đa 10 MB / ảnh
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) onPick(e.target.files);
          // Clear the input so picking the same file again re-fires.
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable grid of thumbnails
// ---------------------------------------------------------------------------

function SortableGrid({
  images,
  onReorder,
  onRemove,
}: {
  images: readonly PendingImage[];
  onReorder: (next: PendingImage[]) => void;
  onRemove: (id: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const ids = useMemo(() => images.map((i) => i.id), [images]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = images.findIndex((i) => i.id === active.id);
    const toIdx = images.findIndex((i) => i.id === over.id);
    if (fromIdx < 0 || toIdx < 0) return;
    onReorder(arrayMove([...images], fromIdx, toIdx));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        {/* data-no-swipe: the parent wizard treats horizontal pointer
            gestures as step swipes on mobile. We opt the grid out so
            drag-to-reorder doesn't double as "go to next step". */}
        <ul
          data-no-swipe
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
        >
          {images.map((image, index) => (
            <SortableThumbnail
              key={image.id}
              image={image}
              index={index}
              isPrimary={index === 0}
              onRemove={() => onRemove(image.id)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableThumbnail({
  image,
  index,
  isPrimary,
  onRemove,
}: {
  image: PendingImage;
  index: number;
  isPrimary: boolean;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative aspect-[4/3] touch-none select-none overflow-hidden rounded-xl border bg-muted",
        isPrimary ? "border-primary" : "border-border",
        isDragging && "z-50 opacity-60 shadow-lg",
      )}
      data-testid={`pending-image-${image.id}`}
    >
      <img
        src={image.previewUrl}
        alt={image.file.name}
        loading="lazy"
        draggable={false}
        aria-label={`Kéo để sắp xếp ảnh ${index + 1}`}
        className="h-full w-full cursor-grab object-cover active:cursor-grabbing"
        {...attributes}
        {...listeners}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Xoá ảnh ${index + 1}`}
        className="absolute top-1.5 right-1.5 inline-flex size-6 items-center justify-center rounded-full bg-destructive/90 text-destructive-foreground transition-opacity hover:bg-destructive"
      >
        <X aria-hidden="true" className="size-3" />
      </button>
      {isPrimary ? (
        <span className="absolute bottom-1.5 left-1.5 rounded bg-primary px-1.5 py-0.5 font-mono text-[11px] font-bold text-primary-foreground">
          ẢNH CHÍNH
        </span>
      ) : null}
    </li>
  );
}
